import { Clock } from "lucide-react";
import { useMemo } from "react";

import { ColocaProgress, type ColocaProgressTone } from "@/shared/components/feedback/ColocaProgress";
import { useQuoteCountdown } from "@/shared/hooks/useQuoteCountdown";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { QUOTE_ANNOUNCE_AT_SECONDS } from "../../constants/cryptoSwaps.constants";
import type { OtcQuote } from "../../types/cryptoSwaps.types";
import { formatCountdown } from "../../utils/cryptoSwapFormat";
import {
  quoteDeadlineMs,
  quoteStage,
  quoteUsableLifeMs,
  type QuoteStage,
} from "../../utils/cryptoSwapStatus";

export interface QuoteTimerBarProps {
  /** `null` renders the idle state. */
  quote: Pick<OtcQuote, "created_at" | "expires_at"> | null;
  /** A re-quote is in flight — the bar says so instead of counting an obsolete price down. */
  isPricing: boolean;
  /** Browser-to-server clock correction, so the countdown agrees with the submit guard. */
  clockOffsetMs?: number;
  label?: string;
  /** Only one bar on screen may own the live region, or every threshold is announced twice. */
  announce?: boolean;
}

const STAGE_TONE: Record<QuoteStage, string> = {
  calm: "text-success-container-foreground",
  warn: "text-warning",
  critical: "text-destructive",
  expired: "text-destructive",
};

const STAGE_FILL: Record<QuoteStage, ColocaProgressTone> = {
  calm: "success",
  warn: "warning",
  critical: "danger",
  expired: "danger",
};

/** Display only. Mount one per quote (`key`): the shared countdown's clock lags a new target by a tick. */
export function QuoteTimerBar({
  quote,
  isPricing,
  clockOffsetMs = 0,
  label,
  announce = false,
}: QuoteTimerBarProps) {
  const { t } = useTranslations("cryptoSwaps");
  const deadlineMs = quoteDeadlineMs(quote);
  const lifeMs = quoteUsableLifeMs(quote);
  // The shared countdown reads the browser clock, so the correction shifts the deadline instead.
  const deadlineIso = useMemo(
    () => (deadlineMs === null ? null : new Date(deadlineMs - clockOffsetMs).toISOString()),
    [deadlineMs, clockOffsetMs],
  );
  const { timeRemaining } = useQuoteCountdown(deadlineIso);

  const priced = deadlineMs !== null && !isPricing;
  const stage = priced ? quoteStage(timeRemaining, lifeMs) : "calm";
  const percent = priced && lifeMs ? Math.max(0, Math.min(100, (timeRemaining / lifeMs) * 100)) : 0;

  const caption = isPricing
    ? t("quote_pricing_badge")
    : deadlineMs === null
      ? t("quote_no_price_badge")
      : stage === "expired"
        ? t("quote_expired_badge")
        : t("quote_valid_for", { time: formatCountdown(timeRemaining) });

  const secondsLeft = Math.ceil(timeRemaining / 1000);
  const announcement = !priced
    ? ""
    : stage === "expired"
      ? t("quote_announce_expired")
      : QUOTE_ANNOUNCE_AT_SECONDS.includes(secondsLeft)
        ? t("quote_announce_seconds", { seconds: secondsLeft })
        : "";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        {label ? (
          <span className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            {label}
          </span>
        ) : (
          <span />
        )}
        <span
          className={cn(
            "inline-flex items-center gap-1.5 font-mono text-[13px] font-medium tabular-nums",
            priced ? STAGE_TONE[stage] : "text-muted-foreground",
          )}
        >
          <Clock aria-hidden="true" className="size-3.5" />
          {caption}
        </span>
      </div>

      <ColocaProgress
        size="sm"
        value={isPricing ? null : percent}
        tone={isPricing ? "neutral" : STAGE_FILL[stage]}
        aria-label={t("quote_timer_label")}
      />

      {announce && (
        <span className="sr-only" aria-live={stage === "expired" ? "assertive" : "polite"}>
          {announcement}
        </span>
      )}
    </div>
  );
}
